import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Hexagon, ShieldCheck, ShieldX, Link2, ArrowRight, Loader2, Boxes } from "lucide-react";
import { http } from "../lib/api";
import { LangSwitch } from "./LangSwitch";
import { ThemeSwitch } from "./ThemeSwitch";
import { Overline } from "./ui";
import { useI18n } from "../lib/i18n";

export default function VerifyPage({ index }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    http
      .get(`/worm/block/${index}`)
      .then((r) => setData(r.data))
      .catch(() => setError(t("vfy.notfound")));
  }, [index, t]);

  const ok = data && data.hash_matches && data.linked_forward;

  return (
    <div className="app-shell min-h-screen px-5 py-10 lg:px-10" data-testid="verify-page">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl border border-aegis-cyan/30 bg-aegis-cyan/10 text-aegis-cyan">
              <Hexagon size={20} strokeWidth={1.6} />
            </span>
            <div>
              <p className="font-heading text-[16px] font-semibold tracking-tight text-slate-100">{t("brand.name")}</p>
              <p className="text-[11px] text-slate-400">{t("vfy.public")}</p>
            </div>
          </div>
          <ThemeSwitch />
          <LangSwitch />
        </div>

        <div className="mt-10">
          <Overline>{t("vfy.overline")}</Overline>
          <h1 className="mt-2 font-heading text-3xl font-light tracking-tighter text-slate-100 sm:text-4xl">
            {t("vfy.title", { n: index })}
          </h1>
          <p className="mt-3 text-sm text-slate-400">{t("vfy.sub")}</p>
        </div>

        {error && (
          <div className="mt-8 rounded-xl border border-aegis-red/40 bg-aegis-red/10 px-5 py-4 text-sm text-aegis-red" data-testid="verify-error">
            {error}
          </div>
        )}

        {!data && !error && (
          <div className="mt-10 flex items-center gap-3 text-sm text-slate-400">
            <Loader2 size={18} className="animate-spin text-aegis-cyan" /> {t("vfy.loading")}
          </div>
        )}

        {data && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-5">
            <div
              className={`flex flex-wrap items-center gap-3 rounded-xl border px-5 py-4 ${
                ok ? "border-aegis-emerald/40 bg-aegis-emerald/10" : "border-aegis-red/40 bg-aegis-red/10"
              }`}
              data-testid="verify-status"
            >
              {ok ? <ShieldCheck className="text-aegis-emerald" size={22} /> : <ShieldX className="text-aegis-red" size={22} />}
              <span className={`text-sm ${ok ? "text-aegis-emerald" : "text-aegis-red"}`}>
                {ok ? t("vfy.ok") : t("vfy.bad")}
              </span>
              <span className="ml-auto font-mono text-[11px] text-slate-400">{data.verified_at}</span>
            </div>

            <div className="rounded-xl border border-edge bg-panel/70 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-sm text-aegis-cyan">BLOCK #{data.block.index}</span>
                <span className="font-mono text-[11px] text-slate-400">
                  <Boxes size={12} className="mr-1 inline" /> {t("vfy.height", { n: data.chain_height })} ·{" "}
                  {t("vfy.count", { n: data.block_count })}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {[
                  [t("vfy.action"), data.block.action_type],
                  [t("vfy.time"), data.block.timestamp],
                  [t("vfy.signer"), data.block.steward_signer],
                ].map(([k, v]) => (
                  <div key={k} className="flex flex-wrap gap-2 border-b border-edge pb-2 last:border-0">
                    <span className="w-32 shrink-0 text-[11px] uppercase tracking-overline text-slate-400">{k}</span>
                    <span className="font-mono text-[12px] text-slate-200">{v}</span>
                  </div>
                ))}
                <div>
                  <span className="text-[11px] uppercase tracking-overline text-slate-400">{t("vfy.hash")}</span>
                  <p className="mt-1 break-all font-mono text-[11px] text-aegis-emerald">{data.block.hash}</p>
                </div>
                <div>
                  <span className="text-[11px] uppercase tracking-overline text-slate-400">{t("vfy.prev")}</span>
                  <p className="mt-1 break-all font-mono text-[11px] text-slate-400">{data.block.prev_hash}</p>
                </div>
                <div className="flex flex-wrap gap-4 pt-1 font-mono text-[11px]">
                  <span className={data.hash_matches ? "text-aegis-emerald" : "text-aegis-red"}>
                    <Link2 size={12} className="mr-1 inline" /> {t("vfy.selfhash")}: {data.hash_matches ? "PASS" : "FAIL"}
                  </span>
                  <span className={data.linked_forward ? "text-aegis-emerald" : "text-aegis-red"}>
                    <Link2 size={12} className="mr-1 inline" /> {t("vfy.forward")}: {data.linked_forward ? "PASS" : "FAIL"}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-edge well p-5">
              <Overline>{t("vfy.payload")}</Overline>
              <pre className="no-scrollbar mt-2 overflow-x-auto font-mono text-[11px] leading-relaxed text-slate-300">
{JSON.stringify(data.block.payload, null, 2)}
              </pre>
            </div>

            <a
              href={window.location.origin}
              data-testid="verify-console-link"
              className="inline-flex items-center gap-2 rounded-lg border border-aegis-cyan/30 bg-aegis-cyan/10 px-4 py-2.5 text-sm text-aegis-cyan transition-colors duration-200 hover:bg-aegis-cyan/20"
            >
              {t("vfy.console")} <ArrowRight size={15} />
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}
